import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Building2,
  LifeBuoy,
  LayoutDashboard,
  LogOut,
  Settings as SettingsIcon,
  User,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { api } from "@/lib/api";
import { usePublicMeta } from "@/lib/publicMeta";
import { SUPPORT_CHANGED, onChange } from "@/lib/events";
import { TopBar } from "@/components/TopBarActions";
import { cn } from "@/lib/utils";


/**
 * Two counts in one place: the filled one is what's waiting on you, the
 * outlined one is what's already in hand. A single count that included both
 * stayed lit after you'd replied, which taught you to ignore it.
 */
function NavBadge({ action = 0, pending = 0 }) {
  if (!action && !pending) return null;
  return (
    <span className="ml-auto flex items-center gap-1 shrink-0">
      {action > 0 && (
        <span
          title={`${action} new — not opened yet`}
          className="bg-brand text-white text-[10.5px] font-mono rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
        >
          {action > 9 ? "9+" : action}
        </span>
      )}
      {pending > 0 && (
        <span
          title={`${pending} open, already seen`}
          className="border border-line text-muted2 text-[10.5px] font-mono rounded-full px-1.5 py-[1px] min-w-[18px] text-center"
        >
          {pending > 9 ? "9+" : pending}
        </span>
      )}
    </span>
  );
}

const NAV = [
  { to: "/overview", label: "Overview", icon: LayoutDashboard },
  { to: "/accounts", label: "Accounts", icon: Building2 },
  { to: "/support-inbox", label: "Support", icon: LifeBuoy, badge: "support" },
  { to: "/platform", label: "Platform settings", icon: SettingsIcon },
  { to: "/profile", label: "Your profile", icon: User },
];

/**
 * The owner's shell. Deliberately separate from the client Layout — this side
 * is about running the platform, not messaging, so it doesn't carry Messages
 * and the rest.
 */
export default function OwnerLayout({ children, title, subtitle, action }) {
  const { user, logout } = useAuth();
  const { setTenantId, tenants } = useTenant();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const meta = usePublicMeta();
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState({ waiting: 0, working: 0 });

  // A waiting ticket is easy to miss, so it's counted in the menu. The two
  // numbers are mutually exclusive — New and Working on it — so together they
  // equal the number of unresolved tickets.
  useEffect(() => {
    let cancelled = false;
    const check = () =>
      api
        .get("/support/summary")
        .then(
          ({ data }) =>
            !cancelled &&
            // Solid = tickets with a client message you haven't opened yet;
            // outline = everything else still unresolved. Opening a ticket
            // moves it from the first number to the second.
            setTickets({
              waiting: data.unseen || 0,
              working: Math.max((data.open || 0) - (data.unseen || 0), 0),
            })
        )
        .catch(() => {});
    check();
    const timer = setInterval(check, 20000);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    // Acting on a ticket updates the badge straight away rather than at the
    // next poll.
    const off = onChange(SUPPORT_CHANGED, check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      off();
    };
  }, [pathname]);

  const doLogout = async () => {
    setTenantId(null);
    await logout();
    navigate("/login");
  };

  const sidebar = (
    <>
      <div className="h-[68px] flex items-center px-5 border-b border-line">
        <Link to="/overview" className="flex items-center gap-2.5 min-w-0">
          {meta.logo ? (
            <img
              src={meta.logo}
              alt=""
              className="w-9 h-9 rounded-xl object-contain shrink-0 shadow-sm bg-card"
            />
          ) : (
            <div className="w-9 h-9 rounded-xl bg-brand flex items-center justify-center shrink-0 shadow-sm">
              <Building2 className="w-[18px] h-[18px] text-white" />
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display text-[16px] font-semibold tracking-[-0.01em] text-ink leading-none truncate">
              Platform
            </div>
            <div className="eyebrow mt-1 leading-none">Owner</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {NAV.map((n) => {
          const active = pathname === n.to;
          return (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setOpen(false)}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13.5px] font-medium transition-colors",
                active
                  ? "bg-brand-light text-brand"
                  : "text-muted2 hover:bg-brand-light/50 hover:text-ink"
              )}
            >
              <n.icon className="w-[17px] h-[17px] shrink-0" />
              {n.label}
              {n.badge === "support" && (
                <NavBadge action={tickets.waiting} pending={tickets.working} />
              )}
            </Link>
          );
        })}

        {tenants.length > 0 && (
          <div className="pt-4 mt-3 border-t border-line/70">
            <div className="eyebrow px-3.5 pb-2">Open a client</div>
            {tenants.slice(0, 8).map((t) => (
              <button
                key={t.tenant_id}
                onClick={() => {
                  setTenantId(t.tenant_id);
                  setOpen(false);
                  navigate("/dashboard");
                }}
                className="w-full flex items-center gap-3 px-3.5 py-2 rounded-xl text-[13px] text-muted2 hover:bg-brand-light/50 hover:text-ink transition-colors text-left"
              >
                <span className="w-[17px] shrink-0 font-mono text-[10px] text-gold font-semibold">
                  {(t.settings?.code_prefix || "IN").slice(0, 2)}
                </span>
                <span className="truncate flex-1">{t.name}</span>
              </button>
            ))}
          </div>
        )}
      </nav>

      <div className="p-3 border-t border-line">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl">
          <Link
            to="/profile"
            onClick={() => setOpen(false)}
            className="w-9 h-9 rounded-full bg-brand-light flex items-center justify-center font-display font-semibold text-brand text-[13px] shrink-0 hover:ring-2 hover:ring-brand-ring transition-all"
            title="Your profile"
          >
            {(user?.name?.[0] || "U").toUpperCase()}
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-medium text-ink truncate">{user?.name}</div>
            <div className="text-[11.5px] text-muted2">Platform owner</div>
          </div>
          <button
            onClick={doLogout}
            title="Sign out"
            className="text-muted2 hover:text-stock-out transition-colors p-1.5 rounded-lg hover:bg-rose-50"
          >
            <LogOut className="w-[17px] h-[17px]" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-surface">
      <aside className="hidden lg:flex w-[248px] bg-card border-r border-line flex-col fixed h-screen z-30">
        {sidebar}
      </aside>

      {open && (
        <>
          <div
            className="lg:hidden fixed inset-0 bg-ink/30 backdrop-blur-sm z-40"
            onClick={() => setOpen(false)}
          />
          <aside className="lg:hidden fixed inset-y-0 left-0 w-[264px] bg-card border-r border-line flex flex-col z-50 animate-rise">
            {sidebar}
          </aside>
        </>
      )}

      <div className="lg:ml-[248px] min-w-0">
        <TopBar
          title={title}
          subtitle={subtitle}
          action={action}
          onMenu={() => setOpen((v) => !v)}
          menuOpen={open}
          scope="platform"
          searchPlaceholder="Search accounts and tickets"
          showBackup
        />
        <main className="px-4 sm:px-7 py-6 pb-16 min-w-0">{children}</main>
      </div>
    </div>
  );
}
